export interface Subsection {
  id: string;
  name: string;
  html: string;
}

export interface Section {
  id: string;
  name: string;
  html: string;
  subsections: Subsection[];
}

/**
 * Extracts sections and subsections from an HTML string.
 * - Sections start at <div class="text-content"> containing "Section".
 * - Subsections start at <div class="section-header"> (exact match only).
 * - For the first subsection inside each section:
 *   - Prepend the HTML between section header and the first subsection header.
 */
export function extractSectionsAndSubsections(html: string): Section[] {
  const sectionRegex =
    /<div[^>]*class=['"][^'"]*\btext-content\b[^'"]*['"][^>]*>(.*?)<\/div>/gi;
  const subsectionRegex =
    /<div[^>]*class=['"](section-header)(?:\s+[^'"]*)?['"][^>]*>(.*?)<\/div>/gi;

  const matches = [
    ...html.matchAll(sectionRegex),
    ...html.matchAll(subsectionRegex),
  ]
    .map((m) => ({
      index: m.index ?? 0,
      full: m[0],
      text: m[2]?.trim() || m[1]?.trim(),
      type:
        m[1] === "section-header"
          ? "subsection"
          : /section\s*\d+/i.test(m[1] ?? m[2] ?? "")
            ? "section"
            : "text",
    }))
    .filter((m) => m.type !== "text")
    .sort((a, b) => a.index - b.index);

  const sections: Section[] = [];
  let currentSection: Section | null = null;
  let currentSubsection: Subsection | null = null;
  let lastIndex = 0;
  let firstSubsection = false;

  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const matchEnd = (match.index ?? 0) + match.full.length;

    if (match.type === "section") {
      // Close current subsection if needed
      if (currentSubsection && currentSection) {
        currentSubsection.html += html.slice(lastIndex, match.index).trim();
        currentSection.subsections.push(currentSubsection);
        currentSubsection = null;
      }

      // Close current section if needed
      if (currentSection) {
        currentSection.html += html.slice(lastIndex, match.index).trim();
        sections.push(currentSection);
      }

      const decodedName = decodeHtml(match.text)
        .replace(/<\/?p[^>]*>/gi, "")
        .trim();
      const id = generateId(decodedName);
      currentSection = {
        id,
        name: decodedName,
        html: match.full,
        subsections: [],
      };
      lastIndex = matchEnd;
      firstSubsection = true; // reset flag for this section
    } else if (match.type === "subsection" && currentSection) {
      // Handle previous subsection
      if (currentSubsection) {
        currentSubsection.html += html.slice(lastIndex, match.index).trim();
        currentSection.subsections.push(currentSubsection);
      }

      const decodedName = decodeHtml(match.text)
        .replace(/<\/?p[^>]*>/gi, "")
        .trim();
      const id = generateId(decodedName);
      let subsectionHtml = match.full;

      // For the FIRST subsection in this section:
      if (firstSubsection) {
        const sectionToSubsectionHTML = html
          .slice(lastIndex, match.index)
          .trim();
        subsectionHtml = sectionToSubsectionHTML + subsectionHtml;
        firstSubsection = false;
      }

      currentSubsection = {
        id,
        name: decodedName,
        html: subsectionHtml,
      };

      lastIndex = matchEnd;
    }

    // Handle last remaining content
    if (i === matches.length - 1) {
      const remaining = html.slice(matchEnd).trim();
      if (currentSubsection && currentSection) {
        currentSubsection.html += remaining;
        currentSection.subsections.push(currentSubsection);
      } else if (currentSection) {
        currentSection.html += remaining;
      }
    }
  }

  if (currentSection && !sections.includes(currentSection)) {
    sections.push(currentSection);
  }

  return sections;
}

function generateId(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
}


export const decodeHtml = (html: string) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

/**
 * Counts form fields in HTML content
 */
export function countFormFields(html: string): number {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Count all form input elements
  const inputs = tempDiv.querySelectorAll("input, textarea, select");

  // Filter out buttons and hidden inputs
  let count = 0;
  inputs.forEach((input) => {
    if (input instanceof HTMLInputElement) {
      // Skip button, submit, reset, hidden inputs
      if (!["button", "submit", "reset", "hidden"].includes(input.type)) {
        count++;
      }
    } else {
      // textarea and select elements
      count++;
    }
  });

  return count;
}

/**
 * Get comprehensive form statistics from extracted sections
 */
export function getFormStats(sections: Section[]) {
  const sectionCount = sections.length;
  const subsectionCount = sections.reduce((acc, section) => acc + section.subsections.length, 0);

  let totalFields = 0;
  sections.forEach(section => {
    if (section.subsections.length > 0) {
      // Count fields in subsections
      section.subsections.forEach(subsection => {
        totalFields += countFormFields(subsection.html);
      });
    } else {
      // Count fields in section directly
      totalFields += countFormFields(section.html);
    }
  });

  return {
    sectionCount,
    subsectionCount,
    totalFields
  };
}